import { useState, useEffect, useMemo } from 'react';
import Modal from '../common/Modal';
import Alert from '../common/Alert';
import { useCreateCategory, useUpdateCategory } from '../../hooks/useQueries';
import IconPicker, { getIconComponent } from '../common/IconPicker';
// import ColorPicker from '../common/ColorPicker';
import { suggestIconAndColor } from '../../utils/categorySuggestions';
import MobileSelect from '../common/MobileSelect';
import { Sparkles, Calendar, CalendarDays, CalendarRange } from 'lucide-react';

const initialFormData = {
  name: '',
  type: 'expense',
  color: '#6366f1',
  icon: '🏷️',
  budget: '',
  budgetPeriod: 'monthly'
};

export default function AddCategoryModal({
  isOpen,
  onClose,
  onSuccess,
  category = null
}) {
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [hasManuallySetIcon, setHasManuallySetIcon] = useState(false);
  const [hasManuallySetColor, setHasManuallySetColor] = useState(false);

  const isEditing = category && category._id;

  // Get suggestion based on category name
  const suggestion = useMemo(() => {
    if (!formData.name || formData.name.length < 2) return null;
    return suggestIconAndColor(formData.name, formData.type);
  }, [formData.name, formData.type]);

  // Check if suggestion is different from current values
  const hasSuggestion = useMemo(() => {
    if (!suggestion) return false;
    const iconDifferent = suggestion.icon !== formData.icon;
    const colorDifferent = suggestion.color !== formData.color;
    return iconDifferent || colorDifferent;
  }, [suggestion, formData.icon, formData.color]);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditing) {
      setFormData({
        name: category.name,
        type: category.type,
        color: category.color || '#6366f1',
        icon: category.icon || 'tag',
        budget: category.budget ?? '',
        budgetPeriod: category.budgetPeriod || 'monthly'
      });
      setHasManuallySetIcon(true);
      setHasManuallySetColor(true);
    } else if (category?.type) {
      // New category with pre-selected type
      setFormData({
        ...initialFormData,
        type: category.type
      });
      setHasManuallySetIcon(false);
      setHasManuallySetColor(false);
    } else {
      setFormData(initialFormData);
      setHasManuallySetIcon(false);
      setHasManuallySetColor(false);
    }
    setValidationErrors({});
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, category?._id, category?.type]);

  // Auto-apply suggestion when name changes (if user hasn't manually set values)
  useEffect(() => {
    if (!isEditing && suggestion && formData.name.length >= 3) {
      setFormData(prev => ({
        ...prev,
        icon: hasManuallySetIcon ? prev.icon : suggestion.icon,
        color: hasManuallySetColor ? prev.color : suggestion.color
      }));
    }
  }, [suggestion, isEditing, hasManuallySetIcon, hasManuallySetColor, formData.name.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleIconChange = (icon) => {
    setFormData((prev) => ({ ...prev, icon }));
    setHasManuallySetIcon(true);
  };

  const handleColorChange = (color) => {
    setFormData((prev) => ({ ...prev, color }));
    setHasManuallySetColor(true);
  };

  const applySuggestion = () => {
    if (suggestion) {
      setFormData((prev) => ({
        ...prev,
        icon: suggestion.icon,
        color: suggestion.color
      }));
      setHasManuallySetIcon(false);
      setHasManuallySetColor(false);
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Category name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (formData.name.length > 50) {
      errors.name = 'Name cannot exceed 50 characters';
    }

    // Validate budget if provided (expense only)
    if (formData.type === 'expense' && formData.budget !== '') {
      const budgetValue = parseFloat(formData.budget);
      if (isNaN(budgetValue) || budgetValue < 0) {
        errors.budget = 'Budget must be a positive number';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setError(null);

    try {
      // Prepare data with proper budget formatting
      const submitData = {
        ...formData,
        budget: formData.type === 'expense' && formData.budget !== ''
          ? parseFloat(formData.budget)
          : null
      };

      // Only include budgetPeriod for expense categories with a budget
      if (formData.type !== 'expense' || formData.budget === '') {
        delete submitData.budgetPeriod;
      }

      let result;
      if (isEditing) {
        result = await updateCategoryMutation.mutateAsync({ id: category._id, data: submitData });
      } else {
        result = await createCategoryMutation.mutateAsync(submitData);
      }

      // Call onSuccess with the new/updated category if provided
      if (onSuccess) {
        onSuccess(result || submitData);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const SuggestedIcon = suggestion ? getIconComponent(suggestion.icon) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Category' : 'Add Category'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Category Type (Expense first as most common) */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() =>
              handleChange({ target: { name: 'type', value: 'expense' } })
            }
            disabled={isEditing && category.isDefault}
            className={`py-3 px-4 rounded-lg font-medium transition-all ${
              formData.type === 'expense'
                ? 'bg-expense-100 text-expense-700 ring-2 ring-expense-500'
                : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
            } ${isEditing && category.isDefault ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Expense
          </button>
          <button
            type="button"
            onClick={() =>
              handleChange({ target: { name: 'type', value: 'income' } })
            }
            disabled={isEditing && category.isDefault}
            className={`py-3 px-4 rounded-lg font-medium transition-all ${
              formData.type === 'income'
                ? 'bg-income-100 text-income-700 ring-2 ring-income-500'
                : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
            } ${isEditing && category.isDefault ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Income
          </button>
        </div>

        {/* Category Name */}
        <div>
          <label className="label">Category Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Salary, Food & Dining"
            enterKeyHint="done"
            className={`input ${validationErrors.name ? 'input-error' : ''}`}
          />
          {validationErrors.name && (
            <p className="text-expense-600 text-sm mt-1">
              {validationErrors.name}
            </p>
          )}
        </div>

        {/* Suggestion Banner */}
        {!isEditing && hasSuggestion && (hasManuallySetIcon || hasManuallySetColor) && (
          <button
            type="button"
            onClick={applySuggestion}
            className="w-full flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-xl hover:bg-primary-100 transition-colors text-left"
          >
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${suggestion.color}20` }}
            >
              <SuggestedIcon className="w-5 h-5" style={{ color: suggestion.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-primary-700 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                Suggested for "{formData.name}"
              </p>
              <p className="text-xs text-primary-600">Tap to apply</p>
            </div>
          </button>
        )}

        {/* Icon */}
        <div>
          <label className="label">Icon</label>
          <IconPicker
            value={formData.icon}
            onChange={handleIconChange}
            onColorChange={handleColorChange}
            color={formData.color}
          />
        </div>
        {/* <div>
          <label className="label">Color</label>
          <ColorPicker
            value={formData.color}
            onChange={handleColorChange}
          />
        </div> */}

        {/* Budget (Expense only) */}
        {formData.type === 'expense' && (
          <div className="space-y-3 p-3 bg-surface-50 rounded-lg">
            <div>
              <label className="label">Budget (Optional)</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500">₹</span>
                  <input
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="10,000"
                    min="0"
                    step="100"
                    className={`input pl-7 ${validationErrors.budget ? 'input-error' : ''}`}
                  />
                </div>
                <div className="w-32">
                  <MobileSelect
                    value={formData.budgetPeriod}
                    onChange={(value) => handleChange({ target: { name: 'budgetPeriod', value } })}
                    label="Budget Period"
                    columns={3}
                    options={[
                      { value: 'monthly', label: 'Monthly', icon: Calendar },
                      { value: 'quarterly', label: 'Quarterly', icon: CalendarDays },
                      { value: 'yearly', label: 'Yearly', icon: CalendarRange }
                    ]}
                  />
                </div>
              </div>
              {validationErrors.budget && (
                <p className="text-expense-600 text-sm mt-1">
                  {validationErrors.budget}
                </p>
              )}
              <p className="text-xs text-surface-500 mt-1">
                Set a spending limit to track your budget
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-surface-200">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary flex-1"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`flex-1 ${
              formData.type === 'income' ? 'btn-income' : 'btn-expense'
            }`}
            disabled={loading}
          >
            {loading
              ? 'Saving...'
              : isEditing
              ? 'Update'
              : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
